import { DiamondCanvas } from 'classes/diamond-canvas';
import {
  BATTED_BALL_TYPE_COLORS,
  COLD_RGB,
  CONTACT_QUALITY_COLORS,
  HEATMAP_RGB,
  HOT_RGB,
  NEUTRAL_RGB,
  NEUTRAL_RGB_HEX,
  PITCH_TYPE_ORDER,
  PITCH_TYPE_PALETTE,
  SESSION_HIT_RESULT_COLORS,
  type RGB,
} from 'enums/session-recap/colors';
import {
  BATTERED_BALL_TYPE_LABELS,
  BattedBallType,
  CONTACT_QUALITY_LABELS,
  ContactQuality,
  EXIT_VELO_SCALE,
  HARD_HIT_MPH,
  INFIELD_DEPTH_FT,
  PitchLocation,
  RecapColorBy,
  RecapZoneFilterKind,
  SESSION_HIT_RESULTS,
  SESSION_HIT_RESULT_LABELS,
  SPRAY_CENTER_DEG,
  SWEET_SPOT_LA,
  SessionHitResult,
  SprayDepth,
  SprayDirection,
  SwingDecision,
  ZoneMetric,
  ZoneMetricFamily,
} from 'enums/session-recap/enums';
import { MAP_COLOR_BY_TO_LABEL } from 'enums/session-recap/labels';
import { IDotColor, MUTED } from 'interfaces/session-recap/i-dot-color';
import { IHeatmapBounds } from 'interfaces/session-recap/i-heatmap-bounds';
import { IHeatmapGrid } from 'interfaces/session-recap/i-heatmap-grid';
import { ILegendItem } from 'interfaces/session-recap/i-legend-item';
import { IRecapZoneFilter } from 'interfaces/session-recap/i-recap-zone-filter';
import { ISessionRecapFilters } from 'interfaces/session-recap/i-session-recap-filters';
import { ISessionRecapHit } from 'interfaces/session-recap/i-session-recap-hit';
import { ISessionRecapStats } from 'interfaces/session-recap/i-session-recap-stats';
import { ISprayZoneCell } from 'interfaces/session-recap/i-spray-zone-cell';
import { IZoneCell } from 'interfaces/session-recap/i-zone-cell';
import { IZoneMetricDef } from 'interfaces/session-recap/i-zone-metric-def';
import { BallHelper } from 'lib_ts/classes/ball.helper';
import {
  DEG_TO_RAD,
  isInRange,
  safeGetRatio,
} from 'lib_ts/classes/math.utilities';
import { PlateHelper } from 'lib_ts/classes/plate.helper';
import {
  HitterSide,
  MAP_OUTCOME_TO_PRETTY_LABEL,
  MAX_FOUL_H_LAUNCH_DEG,
  OutcomeCode,
  getSwingImpliedByOutcome,
} from 'lib_ts/enums/hitters.enums';
import { MAX_HIT_DISTANCE_FT, PitchType } from 'lib_ts/enums/pitches.enums';
import { IStrikeZone } from 'lib_ts/interfaces/i-strike-zone';
import { IPlateLoc } from 'lib_ts/interfaces/pitches/i-plate-loc';
import { ISessionHit } from 'lib_ts/interfaces/session-hits/i-session-hit';
import { clamp, compact, isNil, mean, sortBy, uniq } from 'lodash';

const HIT_RESULTS = [
  SessionHitResult.Single,
  SessionHitResult.Double,
  SessionHitResult.Triple,
  SessionHitResult.HomeRun,
];

const TOTAL_BASES: Partial<Record<SessionHitResult, number>> = {
  [SessionHitResult.Single]: 1,
  [SessionHitResult.Double]: 2,
  [SessionHitResult.Triple]: 3,
  [SessionHitResult.HomeRun]: 4,
};

const formatAvg = (v: number) => v.toFixed(3).replace(/^0+/, '');
// from 0-1 to 0-100%
const formatPercent = (v: number) => `${Math.round(v * 100)}%`;
const formatMph = (v: number) => v.toFixed(1);

// todo: if we are never going to get this any other way, then we don't need this fn vs accessing the hit directly
const getExitMPH = (r: ISessionRecapHit) => r.hit.hit?.exitMPH;
// todo: if we are never going to get this any other way, then we don't need this fn vs accessing the hit directly
const getVLaunchDEG = (r: ISessionRecapHit) => r.hit.hit?.vLaunchDEG;

/** fair ball with a recorded exit velocity */
const isFairWithEV = (r: ISessionRecapHit) =>
  r.isBallInPlay && !isNil(getExitMPH(r));

/** fair ball with a recorded launch angle */
const isFairWithLA = (r: ISessionRecapHit) =>
  r.isBallInPlay && !isNil(getVLaunchDEG(r));

/** fair ball we could classify as ground ball, line drive, fly ball, or popup */
const isFairWithBattedBallType = (r: ISessionRecapHit) =>
  r.isBallInPlay && !!r.battedBallType;

const hasContactOrFoul = (r: ISessionRecapHit) =>
  r.isContact || r.result === SessionHitResult.Foul;

const createZoneMetricRating = (
  partial: Omit<
    IZoneMetricDef,
    'aggregate' | 'format' | 'higherIsBetter' | 'requiresSwing'
  > &
    Partial<Pick<IZoneMetricDef, 'higherIsBetter' | 'format' | 'requiresSwing'>>
): IZoneMetricDef => ({
  aggregate: 'mean',
  higherIsBetter: true,
  requiresSwing: true,
  format: formatPercent,
  ...partial,
});

const createZoneMetricTally = (
  partial: Omit<
    IZoneMetricDef,
    | 'aggregate'
    | 'format'
    | 'showBaseline'
    | 'value'
    | 'higherIsBetter'
    | 'requiresSwing'
  > &
    Partial<Pick<IZoneMetricDef, 'value' | 'higherIsBetter' | 'requiresSwing'>>
): IZoneMetricDef => ({
  ...partial,
  aggregate: 'count',
  showBaseline: false,
  higherIsBetter: partial.higherIsBetter ?? true,
  requiresSwing: partial.requiresSwing ?? true,
  value: partial.value ?? (() => 1),
  format: (v) => `${Math.round(v)}`,
});

const ZONE_METRIC_SECTIONS = [
  {
    family: ZoneMetricFamily.Pitches,
    ids: [ZoneMetric.PitchRate, ZoneMetric.Pitches],
  },
  {
    family: ZoneMetricFamily.Swing,
    ids: [ZoneMetric.Swing, ZoneMetric.Swings, ZoneMetric.Chase],
  },
  {
    family: ZoneMetricFamily.Contact,
    ids: [
      ZoneMetric.Contact,
      ZoneMetric.Contacts,
      ZoneMetric.Whiff,
      ZoneMetric.Whiffs,
    ],
  },
  {
    family: ZoneMetricFamily.BattedBall,
    ids: [
      ZoneMetric.BattedBalls,
      ZoneMetric.GroundBall,
      ZoneMetric.LineDrive,
      ZoneMetric.FlyBall,
      ZoneMetric.Popup,
    ],
  },
  {
    family: ZoneMetricFamily.Quality,
    ids: [
      ZoneMetric.HardHit,
      ZoneMetric.HardHits,
      ZoneMetric.BarrelRate,
      ZoneMetric.Barrels,
    ],
  },
  {
    family: ZoneMetricFamily.Trajectory,
    ids: [
      ZoneMetric.ExitVelo,
      ZoneMetric.MaxExitVelo,
      ZoneMetric.LaunchAngle,
      ZoneMetric.SweetSpot,
    ],
  },
  {
    family: ZoneMetricFamily.Production,
    ids: [ZoneMetric.Bacon, ZoneMetric.XBacon, ZoneMetric.XWobacon],
  },
];

export const ZONE_METRICS: IZoneMetricDef[] = [
  {
    id: ZoneMetric.PitchRate,
    label: 'hitters.recap-pitch-pct',
    include: () => true,
    value: () => 1,
    aggregate: 'share',
    showBaseline: false,
    min: 0.04,
    baseline: 0.08,
    max: 0.14,
    higherIsBetter: true,
    requiresSwing: false,
    format: formatPercent,
  },
  createZoneMetricTally({
    id: ZoneMetric.Pitches,
    label: 'hitters.recap-total-pitches',
    include: () => true,
    requiresSwing: false,
    min: 1,
    baseline: 5,
    max: 12,
  }),
  createZoneMetricRating({
    id: ZoneMetric.Swing,
    label: 'hitters.recap-swing-pct',
    include: () => true,
    value: (r) => (r.isSwing ? 1 : 0),
    min: 0.35,
    baseline: 0.47,
    max: 0.6,
  }),
  createZoneMetricTally({
    id: ZoneMetric.Swings,
    label: 'hitters.recap-swings',
    include: (r) => r.isSwing,
    min: 1,
    baseline: 3,
    max: 8,
  }),
  createZoneMetricRating({
    id: ZoneMetric.Chase,
    label: 'hitters.recap-chase-pct',
    include: (r) => !!r.hit.pitch?.plate && !r.inZone,
    value: (r) => (r.isSwing ? 1 : 0),
    min: 0.2,
    baseline: 0.28,
    max: 0.36,
    higherIsBetter: false,
  }),
  createZoneMetricRating({
    id: ZoneMetric.Contact,
    label: 'hitters.recap-contact-pct',
    include: (r) => r.isSwing,
    value: (r) => (hasContactOrFoul(r) ? 1 : 0),
    min: 0.68,
    baseline: 0.76,
    max: 0.84,
  }),
  createZoneMetricTally({
    id: ZoneMetric.Contacts,
    label: 'hitters.recap-contacts',
    include: hasContactOrFoul,
    min: 1,
    baseline: 2,
    max: 6,
  }),
  createZoneMetricRating({
    id: ZoneMetric.Whiff,
    label: 'hitters.recap-whiff-pct',
    include: (r) => r.isSwing,
    value: (r) => (r.isWhiff ? 1 : 0),
    min: 0.15,
    baseline: 0.25,
    max: 0.35,
    higherIsBetter: false,
  }),
  createZoneMetricTally({
    id: ZoneMetric.Whiffs,
    label: 'hitters.recap-whiffs',
    include: (r) => r.isWhiff,
    min: 1,
    baseline: 2,
    max: 5,
    higherIsBetter: false,
  }),
  createZoneMetricTally({
    id: ZoneMetric.BattedBalls,
    label: 'hitters.recap-batted-balls',
    include: (r) => r.isBallInPlay,
    min: 1,
    baseline: 2,
    max: 6,
  }),
  createZoneMetricRating({
    id: ZoneMetric.ExitVelo,
    label: 'hitters.recap-avg-exit-velocity',
    include: isFairWithEV,
    value: (r) => getExitMPH(r) ?? 0,
    min: 82,
    baseline: EXIT_VELO_SCALE.baseline,
    max: 95,
    format: formatMph,
  }),
  {
    id: ZoneMetric.MaxExitVelo,
    label: 'hitters.recap-max-exit-velo',
    include: isFairWithEV,
    value: (r) => getExitMPH(r) ?? 0,
    aggregate: 'max',
    showBaseline: false,
    min: 100,
    baseline: 110,
    max: 118,
    higherIsBetter: true,
    requiresSwing: true,
    format: formatMph,
  },
  createZoneMetricRating({
    id: ZoneMetric.LaunchAngle,
    label: 'hitters.recap-avg-launch-angle',
    include: isFairWithLA,
    value: (r) => getVLaunchDEG(r) ?? 0,
    min: 5,
    baseline: 12,
    max: 20,
    format: (v) => `${v.toFixed(1)}°`,
  }),
  createZoneMetricRating({
    id: ZoneMetric.SweetSpot,
    label: 'hitters.recap-sweet-spot-pct',
    include: isFairWithLA,
    value: (r) => {
      const la = getVLaunchDEG(r) ?? 0;
      return la >= SWEET_SPOT_LA.min && la <= SWEET_SPOT_LA.max ? 1 : 0;
    },
    min: 0.25,
    baseline: 0.33,
    max: 0.42,
  }),
  createZoneMetricTally({
    id: ZoneMetric.Barrels,
    label: 'hitters.recap-barrels',
    include: (r) =>
      r.isBallInPlay && r.contactQuality === ContactQuality.Barrel,
    min: 1,
    baseline: 1,
    max: 3,
  }),
  createZoneMetricRating({
    id: ZoneMetric.BarrelRate,
    label: 'hitters.recap-barrel-pct',
    include: (r) => r.isBallInPlay,
    value: (r) => (r.contactQuality === ContactQuality.Barrel ? 1 : 0),
    min: 0.04,
    baseline: 0.08,
    max: 0.14,
  }),
  createZoneMetricTally({
    id: ZoneMetric.HardHits,
    label: 'hitters.recap-hard-hits',
    include: (r) => isFairWithEV(r) && (getExitMPH(r) ?? 0) >= HARD_HIT_MPH,
    min: 1,
    baseline: 2,
    max: 5,
  }),
  createZoneMetricRating({
    id: ZoneMetric.HardHit,
    label: 'hitters.recap-hard-hit-pct',
    include: isFairWithEV,
    value: (r) => ((getExitMPH(r) ?? 0) >= HARD_HIT_MPH ? 1 : 0),
    min: 0.25,
    baseline: 0.38,
    max: 0.5,
  }),
  createZoneMetricRating({
    id: ZoneMetric.GroundBall,
    label: 'hitters.recap-ground-ball-pct',
    include: isFairWithBattedBallType,
    value: (r) => (r.battedBallType === BattedBallType.GroundBall ? 1 : 0),
    min: 0.35,
    baseline: 0.43,
    max: 0.52,
    higherIsBetter: false,
  }),
  createZoneMetricRating({
    id: ZoneMetric.LineDrive,
    label: 'hitters.recap-line-drive-pct',
    include: isFairWithBattedBallType,
    value: (r) => (r.battedBallType === BattedBallType.LineDrive ? 1 : 0),
    min: 0.16,
    baseline: 0.21,
    max: 0.27,
  }),
  createZoneMetricRating({
    id: ZoneMetric.FlyBall,
    label: 'hitters.recap-fly-ball-pct',
    include: isFairWithBattedBallType,
    value: (r) => (r.battedBallType === BattedBallType.FlyBall ? 1 : 0),
    min: 0.18,
    baseline: 0.24,
    max: 0.32,
  }),
  createZoneMetricRating({
    id: ZoneMetric.Popup,
    label: 'hitters.recap-popup-pct',
    include: isFairWithBattedBallType,
    value: (r) => (r.battedBallType === BattedBallType.Popup ? 1 : 0),
    min: 0.03,
    baseline: 0.06,
    max: 0.1,
    higherIsBetter: false,
  }),
  createZoneMetricRating({
    id: ZoneMetric.Bacon,
    label: 'hitters.recap-bacon',
    include: (r) => r.isBallInPlay,
    value: (r) => (r.totalBases > 0 ? 1 : 0),
    min: 0.25,
    baseline: 0.3,
    max: 0.35,
    format: formatAvg,
  }),
  createZoneMetricRating({
    id: ZoneMetric.XBacon,
    label: 'hitters.recap-xbacon',
    include: (r) => r.isBallInPlay && !isNil(r.hit.hit?.xbacon),
    value: (r) => r.hit.hit?.xbacon ?? 0,
    min: 0.25,
    baseline: 0.3,
    max: 0.35,
    format: formatAvg,
  }),
  createZoneMetricRating({
    id: ZoneMetric.XWobacon,
    label: 'hitters.recap-xwobacon',
    include: (r) => r.isBallInPlay && !isNil(r.hit.hit?.xwobacon),
    value: (r) => r.hit.hit?.xwobacon ?? 0,
    min: 0.3,
    baseline: 0.37,
    max: 0.45,
    format: formatAvg,
  }),
];

const hasSwing = (recaps: ISessionRecapHit[]) =>
  recaps.some((r) => r.isSwing && !r.hit.avg_excluded);

const blankZoneCells = (): IZoneCell[] =>
  Array.from({ length: 25 }, (_, i) => ({
    row: Math.floor(i / 5),
    col: i % 5,
    count: 0,
    value: null,
  }));

// linear interpolation between two RGB values
const getLerpRGB = (a: RGB, b: RGB, t: number): RGB => [
  Math.round(a[0] + (b[0] - a[0]) * t),
  Math.round(a[1] + (b[1] - a[1]) * t),
  Math.round(a[2] + (b[2] - a[2]) * t),
];

const MAP_OUTCOME_TO_RESULT: Record<OutcomeCode, SessionHitResult | undefined> =
  {
    [OutcomeCode.Single]: SessionHitResult.Single,
    [OutcomeCode.Double]: SessionHitResult.Double,
    [OutcomeCode.Triple]: SessionHitResult.Triple,
    [OutcomeCode.HomeRun]: SessionHitResult.HomeRun,
    [OutcomeCode.FlyOut]: SessionHitResult.FlyOut,
    [OutcomeCode.GroundOut]: SessionHitResult.GroundOut,
    [OutcomeCode.FoulOut]: SessionHitResult.FoulOut,
    [OutcomeCode.Foul]: SessionHitResult.Foul,
    [OutcomeCode.SwingingStrike]: SessionHitResult.SwingingStrike,
    [OutcomeCode.CalledStrike]: SessionHitResult.CalledStrike,
    [OutcomeCode.Ball]: SessionHitResult.Ball,
    [OutcomeCode.Unknown]: undefined,
  };

export class SessionRecapHelper {
  static hasContact(h: ISessionHit): boolean {
    if (h.hit?._mx_outcome && !getSwingImpliedByOutcome(h.hit._mx_outcome)) {
      // e.g. ball or called strike implies there was no swing, which implies no contact
      return false;
    }

    return !h.hit?.invalid && !!(h.hit?.exitMPH || h.hit?.distanceFT);
  }

  static getOutcome(
    h: ISessionHit,
    szDef: IStrikeZone
  ): OutcomeCode | undefined {
    const { hit, pitch } = h;

    if (hit?._mx_outcome) {
      // always use the mx_outcome if it exists and is not unknown (i.e. "")
      return hit._mx_outcome;
    }

    if (!hit) {
      // shouldn't trigger but just in case
      if (pitch?.plate) {
        // without a hit, there is no swing flag => use plate location to determine the result
        return PlateHelper.isStrike({ plateFt: pitch.plate, szDef })
          ? OutcomeCode.CalledStrike
          : OutcomeCode.Ball;
      }

      // without a hit or plate location => impossible to determine the result
      return undefined;
    }

    // hit exists, but outcome from TDS model is empty => resolve to swinging strike or called strike based on plate location
    if (hit._mx_swing) {
      // plate location doesn't matter if the hitter swung
      return OutcomeCode.SwingingStrike;
    }

    if (pitch?.plate) {
      // if the hitter did not swing, use plate location to determine the result
      return PlateHelper.isStrike({ plateFt: pitch.plate, szDef })
        ? OutcomeCode.CalledStrike
        : OutcomeCode.Ball;
    }

    // no hit, no plate => unknown outcome
    return undefined;
  }

  // uses same logic as getOutcome, but mapped to the session hit result enum
  static getResult(
    h: ISessionHit,
    szDef: IStrikeZone
  ): SessionHitResult | undefined {
    const outcome = SessionRecapHelper.getOutcome(h, szDef);

    if (!outcome) {
      return undefined;
    }

    return MAP_OUTCOME_TO_RESULT[outcome];
  }

  static getPrettyOutcome(
    h: ISessionHit,
    szDef: IStrikeZone
  ): string | undefined {
    const outcome = SessionRecapHelper.getOutcome(h, szDef);

    if (!outcome) {
      return undefined;
    }
    return MAP_OUTCOME_TO_PRETTY_LABEL[outcome];
  }

  static getPitchLocation(plate: IPlateLoc, szDef: IStrikeZone): PitchLocation {
    const halfW = (szDef.right_ft - szDef.left_ft) / 2;
    const halfH = (szDef.top_ft - szDef.bottom_ft) / 2;
    const cx = (szDef.right_ft + szDef.left_ft) / 2;
    const cz = (szDef.top_ft + szDef.bottom_ft) / 2;

    const r = Math.max(
      Math.abs(plate.plate_x - cx) / halfW,
      Math.abs(plate.plate_z - cz) / halfH
    );

    if (r <= 0.67) {
      return PitchLocation.Heart;
    }

    if (r <= 1.33) {
      return PitchLocation.Shadow;
    }

    if (r <= 2) {
      return PitchLocation.Chase;
    }

    return PitchLocation.Waste;
  }

  /** Statcast launch_speed_angle classification (Tango) */
  static getContactQuality(exitMPH: number, launchDEG: number): ContactQuality {
    const ev = exitMPH;
    const la = launchDEG;

    if (
      ev * 1.5 - la >= 117 &&
      ev + la >= 124 &&
      ev >= 98 &&
      la >= 4 &&
      la <= 50
    ) {
      return ContactQuality.Barrel;
    }

    if (
      ev * 1.5 - la >= 111 &&
      ev + la >= 119 &&
      ev >= 95 &&
      la >= 0 &&
      la <= 52
    ) {
      return ContactQuality.Solid;
    }

    if (ev <= 59) {
      return ContactQuality.Weak;
    }

    if (
      (ev * 2 - la >= 87 &&
        la <= 41 &&
        ev * 2 + la <= 175 &&
        ev + la * 1.3 >= 89 &&
        ev >= 59 &&
        la <= 72) ||
      (ev + la * 1.3 <= 112 && ev + la * 1.55 >= 92 && ev >= 72 && la <= 86) ||
      (la <= 20 && ev + la * 2.4 >= 98 && ev >= 86 && ev <= 95) ||
      (ev - la >= 76 && ev + la * 2.4 >= 98 && ev >= 95 && la <= 30)
    ) {
      return ContactQuality.FlareBurner;
    }

    if (ev + la * 2 >= 116) {
      return ContactQuality.Under;
    }

    return ContactQuality.Topped;
  }

  static getBattedBallType(launchDEG: number): BattedBallType {
    if (launchDEG < 10) {
      return BattedBallType.GroundBall;
    }

    if (launchDEG < 25) {
      return BattedBallType.LineDrive;
    }

    if (launchDEG <= 50) {
      return BattedBallType.FlyBall;
    }

    return BattedBallType.Popup;
  }

  /** 1-based sequence by creation time (descending), keyed by hit _id */
  static getPitchNumbers(hits: ISessionHit[]): Record<string, number> {
    return sortBy(hits, (a) => a._created).reduce(
      (acc, h, i) => {
        acc[h._id] = i + 1;
        return acc;
      },
      {} as Record<string, number>
    );
  }

  /**
   * Enriches raw session hits with the normalized fields used by recap metrics
   * and filters. The returned records retain the input order.
   */
  static convertToRecap(
    hits: ISessionHit[],
    szDef: IStrikeZone
  ): ISessionRecapHit[] {
    const numbers = SessionRecapHelper.getPitchNumbers(hits);

    return compact(
      hits.map((h) => {
        try {
          const result = SessionRecapHelper.getResult(h, szDef);
          const isContact = SessionRecapHelper.hasContact(h);
          // Contact necessarily implies a swing, even when swing detection is absent.
          const isSwing = h.hit?._mx_swing || isContact;
          const exitMPH = h.hit?.exitMPH;
          const launchDEG = h.hit?.vLaunchDEG;
          // Contact quality needs both measurements; batted-ball type only needs launch angle.
          const hasLaunch = isContact && !isNil(exitMPH) && !isNil(launchDEG);

          const o: ISessionRecapHit = {
            hit: h,
            pitchNumber: numbers[h._id],
            result: result,
            pitcherHand: BallHelper.getPitcherHand(h.pitch?.release?.px),
            location: h.pitch?.plate
              ? SessionRecapHelper.getPitchLocation(h.pitch.plate, szDef)
              : undefined,
            contactQuality: hasLaunch
              ? SessionRecapHelper.getContactQuality(
                  exitMPH as number,
                  launchDEG as number
                )
              : undefined,
            battedBallType:
              isContact && !isNil(launchDEG)
                ? SessionRecapHelper.getBattedBallType(launchDEG)
                : undefined,
            isSwing: isSwing,
            isContact: isContact,
            // ? no detection and no override should not count as a whiff
            isWhiff: h.hit?._mx_outcome === OutcomeCode.SwingingStrike,
            inZone:
              !!h.pitch?.plate &&
              PlateHelper.isStrike({ plateFt: h.pitch.plate, szDef }),
            // Walks and strikeouts have results but do not put the ball in play.
            isBallInPlay:
              !!result &&
              (HIT_RESULTS.includes(result) ||
                result === SessionHitResult.FlyOut ||
                result === SessionHitResult.GroundOut),
            totalBases: (result && TOTAL_BASES[result]) || 0,
          };

          return o;
        } catch (error) {
          console.error(error);
          return undefined;
        }
      })
    );
  }

  static applyFilters(
    recaps: ISessionRecapHit[],
    filters: ISessionRecapFilters
  ): ISessionRecapHit[] {
    const matchingFn = <T>(selected: T[], value: T | undefined) =>
      selected.length === 0 || (!isNil(value) && selected.includes(value));

    const { advanced } = filters;

    return recaps.filter((r) => {
      const { pitch, hit } = r.hit;

      if (!pitch?._id) {
        return false;
      }

      if (!matchingFn(filters.pitchTypes, pitch.type)) {
        return false;
      }

      if (!matchingFn(filters.outcomes, r.result)) {
        return false;
      }

      if (!matchingFn(filters.pitcherHands, r.pitcherHand)) {
        return false;
      }

      if (!matchingFn(filters.locations, r.location)) {
        return false;
      }

      if (!matchingFn(filters.contactQualities, r.contactQuality)) {
        return false;
      }

      if (
        !matchingFn(
          filters.swings,
          r.isSwing ? SwingDecision.Swing : SwingDecision.Take
        )
      ) {
        return false;
      }

      if (!advanced) {
        return true;
      }

      const hasHitFilters = [
        advanced.exitMPH,
        advanced.vLaunchDEG,
        advanced.distanceFT,
      ].some((f) => f.value);

      if (hasHitFilters && !hit) {
        return false;
      }

      return (
        isInRange(advanced.speedMPH.value, pitch.speedMPH) &&
        isInRange(
          advanced.xBreakIN.value,
          pitch.xBreakIN ? -1 * pitch.xBreakIN : undefined
        ) &&
        isInRange(advanced.zBreakIN.value, pitch.zBreakIN) &&
        isInRange(advanced.exitMPH.value, hit?.exitMPH) &&
        isInRange(advanced.vLaunchDEG.value, hit?.vLaunchDEG) &&
        isInRange(advanced.distanceFT.value, hit?.distanceFT)
      );
    });
  }

  static getStats(recaps: ISessionRecapHit[]): ISessionRecapStats {
    const included = recaps.filter((r) => !r.hit.avg_excluded);
    const swings = included.filter((r) => r.isSwing);
    const contact = swings.filter((r) => r.isContact);
    const whiffs = swings.filter((r) => r.isWhiff);
    const fairBatted = included.filter(
      (r) => r.contactQuality && r.result !== SessionHitResult.Foul
    );

    const pitchMPHs = compact(included.map((r) => r.hit.pitch?.speedMPH));

    return {
      pitches: included.length,
      avgPitchMPH: pitchMPHs.length > 0 ? mean(pitchMPHs) : null,
      swings: swings.length,
      contactRate: safeGetRatio(contact.length, swings.length),
      whiffRate: safeGetRatio(whiffs.length, swings.length),
      barrelRate: safeGetRatio(
        fairBatted.filter((r) => r.contactQuality === ContactQuality.Barrel)
          .length,
        fairBatted.length
      ),
    };
  }

  static getZoneMetricSections(metrics: IZoneMetricDef[]) {
    const byId = new Map(metrics.map((metric) => [metric.id, metric]));

    return ZONE_METRIC_SECTIONS.flatMap((section) => {
      const sectionMetrics = compact(section.ids.map((id) => byId.get(id)));

      return sectionMetrics.length > 0
        ? [{ family: section.family, metrics: sectionMetrics }]
        : [];
    });
  }

  // encapsulated so that it can be easily tested
  static getSortedZoneMetrics() {
    return SessionRecapHelper.getZoneMetricSections(ZONE_METRICS).flatMap(
      (s) => s.metrics
    );
  }

  /**
   * 5x5 grid in catcher's view: inner 3x3 are the strike zone thirds, the outer
   * ring is one cell wide and absorbs anything further out
   */
  static getZoneCell(
    plate: IPlateLoc,
    szDef: IStrikeZone
  ): { row: number; col: number } {
    const cellW = (szDef.right_ft - szDef.left_ft) / 3;
    const cellH = (szDef.top_ft - szDef.bottom_ft) / 3;

    const col = Math.floor((plate.plate_x - szDef.left_ft) / cellW) + 1;
    const row = Math.floor((szDef.top_ft - plate.plate_z) / cellH) + 1;

    return {
      row: clamp(row, 0, 4),
      col: clamp(col, 0, 4),
    };
  }

  /**
   * Aggregates the selected metric into the 5x5 catcher's-view grid.
   * A null cell value means the UI should leave that cell blank.
   */
  static getZoneCells(
    recaps: ISessionRecapHit[],
    metric: IZoneMetricDef,
    szDef: IStrikeZone
  ): IZoneCell[] {
    if (metric.requiresSwing && !hasSwing(recaps)) {
      return blankZoneCells();
    }

    const sums: { count: number; total: number; max: number }[] = Array.from(
      { length: 25 },
      () => ({ count: 0, total: 0, max: Number.NEGATIVE_INFINITY })
    );

    // Denominator for share metrics: every pitch the metric accepts, across the plate.
    let eligible = 0;

    recaps.forEach((recap) => {
      const plate = recap.hit.pitch?.plate;

      // Needs a plate location, inclusion in averages, and this metric's sample.
      if (!plate || recap.hit.avg_excluded || !metric.include(recap)) {
        return;
      }

      eligible++;
      const { row, col } = SessionRecapHelper.getZoneCell(plate, szDef);
      const cell = sums[row * 5 + col];
      const v = metric.value(recap);
      cell.count++;
      cell.total += v;
      if (v > cell.max) {
        cell.max = v;
      }
    });

    return sums.map((s, i) => {
      let value: number | null = null;

      if (metric.aggregate === 'count') {
        value = s.count > 0 ? s.count : null;
      } else if (metric.aggregate === 'max') {
        value = s.count > 0 ? s.max : null;
      } else if (metric.aggregate === 'share') {
        // This cell's share of every pitch the metric accepted.
        value = s.count > 0 && eligible > 0 ? s.count / eligible : null;
      } else if (s.count > 0) {
        value = s.total / s.count;
      }

      return {
        row: Math.floor(i / 5),
        col: i % 5,
        count: s.count,
        value,
      };
    });
  }

  /**
   * Gaussian kernel regression (Nadaraya-Watson) of the metric over the plate.
   * Bandwidth is wide enough that nearby pitches merge into a blob, and alpha
   * follows density so empty plate stays clear.
   */
  static getHeatmap(
    recaps: ISessionRecapHit[],
    metric: IZoneMetricDef,
    bounds: IHeatmapBounds,
    options: { cellFt?: number; bandwidthFt?: number } = {}
  ): IHeatmapGrid {
    const cellFt = options.cellFt ?? 1 / 12;
    // ~4.5 in, so a blob stays with its pitches instead of painting empty plate.
    const bandwidthFt = options.bandwidthFt ?? 0.38;

    const points =
      metric.requiresSwing && !hasSwing(recaps)
        ? []
        : recaps.reduce(
            (acc, r) => {
              const plate = r.hit.pitch?.plate;

              if (plate && !r.hit.avg_excluded && metric.include(r)) {
                acc.push({
                  x: plate.plate_x,
                  z: plate.plate_z,
                  v: metric.value(r),
                });
              }

              return acc;
            },
            [] as { x: number; z: number; v: number }[]
          );

    const cols = Math.ceil((bounds.xMaxFt - bounds.xMinFt) / cellFt);
    const rows = Math.ceil((bounds.zMaxFt - bounds.zMinFt) / cellFt);
    const values: number[] = new Array(cols * rows).fill(NaN);
    const alphas: number[] = new Array(cols * rows).fill(0);

    const twoH2 = 2 * bandwidthFt * bandwidthFt;
    // one pitch is opaque at its centre. The floor is high enough that a
    // pile of distant pitches cannot paint an empty part of the plate.
    const fullDensity = 1;
    const minDensity = 0.55;

    for (let row = 0; row < rows; row++) {
      const z = bounds.zMaxFt - (row + 0.5) * cellFt;

      for (let col = 0; col < cols; col++) {
        const x = bounds.xMinFt + (col + 0.5) * cellFt;

        let wSum = 0;
        let vSum = 0;
        let vMax = Number.NEGATIVE_INFINITY;

        points.forEach((p) => {
          const dx = p.x - x;
          const dz = p.z - z;
          const w = Math.exp(-(dx * dx + dz * dz) / twoH2);
          wSum += w;
          vSum += w * p.v;
          if (w > 0.05 && p.v > vMax) {
            vMax = p.v;
          }
        });

        if (wSum < minDensity) {
          continue;
        }

        const i = row * cols + col;

        if (metric.aggregate === 'count') {
          values[i] = wSum;
        } else if (metric.aggregate === 'share') {
          values[i] = points.length ? wSum / points.length : NaN;
        } else if (metric.aggregate === 'max') {
          values[i] = vMax;
        } else {
          values[i] = vSum / wSum;
        }
        alphas[i] = clamp(
          (wSum - minDensity) / (fullDensity - minDensity),
          0,
          1
        );
      }
    }

    return { ...bounds, cols, rows, values, alphas };
  }

  static getSprayAngleDEG(hit: ISessionHit): number | undefined {
    const deg = hit.hit?.hLandingDEG ?? hit.hit?.hLaunchDEG;
    return isNil(deg) ? undefined : deg;
  }

  /** same eligibility as the hitter spray chart, incl. legacy over-distance hits */
  private static getPlottableDistanceFT(h: ISessionHit): number | undefined {
    const hit = h.hit;

    if (
      isNil(hit) ||
      isNil(hit?.distanceFT) ||
      hit.invalid ||
      hit.distanceFT > MAX_HIT_DISTANCE_FT ||
      !DiamondCanvas.canDrawHit(hit)
    ) {
      return undefined;
    }

    return hit.distanceFT;
  }

  /** feet from home plate, +x towards 1B, +y towards CF */
  static getLandingFt(hit: ISessionHit): { x: number; y: number } | undefined {
    const distanceFT = SessionRecapHelper.getPlottableDistanceFT(hit);
    const deg = SessionRecapHelper.getSprayAngleDEG(hit);

    if (isNil(distanceFT) || isNil(deg)) {
      return undefined;
    }

    const rad = Math.PI / 2 - deg * DEG_TO_RAD;

    return {
      x: Math.cos(rad) * distanceFT,
      y: Math.sin(rad) * distanceFT,
    };
  }

  static getSprayZone(
    hit: ISessionHit,
    side: HitterSide
  ): { direction: SprayDirection; depth: SprayDepth } | undefined {
    const distanceFT = SessionRecapHelper.getPlottableDistanceFT(hit);
    const deg = SessionRecapHelper.getSprayAngleDEG(hit);

    if (!distanceFT || isNil(deg)) {
      return undefined;
    }

    if (Math.abs(deg) > MAX_FOUL_H_LAUNCH_DEG) {
      return undefined;
    }

    // -ve angles are towards 3B, i.e. pull side for a RHH
    const pullSign = side === HitterSide.LHH ? 1 : -1;
    const signed = deg * pullSign;

    const direction =
      signed > SPRAY_CENTER_DEG
        ? SprayDirection.Pull
        : signed < -SPRAY_CENTER_DEG
          ? SprayDirection.Oppo
          : SprayDirection.Center;

    return {
      direction,
      depth:
        distanceFT < INFIELD_DEPTH_FT
          ? SprayDepth.Infield
          : SprayDepth.Outfield,
    };
  }

  static getSprayZones(
    recaps: ISessionRecapHit[],
    side: HitterSide
  ): ISprayZoneCell[] {
    const cells: ISprayZoneCell[] = [];

    [SprayDepth.Infield, SprayDepth.Outfield].forEach((depth) =>
      [SprayDirection.Pull, SprayDirection.Center, SprayDirection.Oppo].forEach(
        (direction) => cells.push({ direction, depth, count: 0, share: 0 })
      )
    );

    let total = 0;

    for (const r of recaps) {
      if (r.hit.avg_excluded) {
        continue;
      }

      const zone = SessionRecapHelper.getSprayZone(r.hit, side);

      if (!zone) {
        continue;
      }

      const cell = cells.find(
        (c) => c.direction === zone.direction && c.depth === zone.depth
      );

      if (cell) {
        cell.count++;
        total++;
      }
    }

    cells.forEach((c) => {
      c.share = total ? c.count / total : 0;
    });

    return cells;
  }

  /** same bins as the 5x5 plate grid and the six spray wedges */
  static isInChartZone(
    recap: ISessionRecapHit,
    filter: IRecapZoneFilter,
    side: HitterSide,
    szDef: IStrikeZone
  ): boolean {
    if (filter.kind === RecapZoneFilterKind.Plate) {
      const plate = recap.hit.pitch?.plate;

      if (!plate) {
        return false;
      }

      const cell = SessionRecapHelper.getZoneCell(plate, szDef);
      return cell.row === filter.row && cell.col === filter.col;
    }

    const zone = SessionRecapHelper.getSprayZone(recap.hit, side);

    if (!zone) {
      return false;
    }

    return zone.direction === filter.direction && zone.depth === filter.depth;
  }

  /** linear blue -> cyan -> green -> yellow -> red across min..max */
  static getHeatmapRGB(
    value: number,
    scale: { min: number; max: number },
    higherIsBetter = true
  ): RGB {
    const span = scale.max - scale.min || 1;
    let t = clamp((value - scale.min) / span, 0, 1);

    if (!higherIsBetter) {
      t = 1 - t;
    }

    const scaled = t * (HEATMAP_RGB.length - 1);
    const index = Math.min(HEATMAP_RGB.length - 2, Math.floor(scaled));

    return getLerpRGB(
      HEATMAP_RGB[index],
      HEATMAP_RGB[index + 1],
      scaled - index
    );
  }

  /** null when this session does not separate a cold cell from a hot one */
  static getSessionColorScale(
    values: number[]
  ): { min: number; max: number } | null {
    if (values.length < 2) {
      return null;
    }

    const min = Math.min(...values);
    const max = Math.max(...values);

    if (min === max) {
      return null;
    }

    return { min, max };
  }

  /**
   * -1 at the cold end, 0 at the baseline, 1 at the hot end.
   * Flipped when a high value is bad for the hitter.
   */
  private static divergingT(
    value: number,
    scale: { min: number; baseline: number; max: number },
    higherIsBetter: boolean
  ): number {
    let t =
      value >= scale.baseline
        ? clamp((value - scale.baseline) / (scale.max - scale.baseline), 0, 1)
        : -clamp((scale.baseline - value) / (scale.baseline - scale.min), 0, 1);

    if (!higherIsBetter) {
      t = -t;
    }

    return t;
  }

  /** blue (cold) -> neutral -> red (hot), from the hitter's perspective */
  static getDivergingRGB(
    value: number,
    scale: { min: number; baseline: number; max: number },
    higherIsBetter = true
  ): RGB {
    const t = SessionRecapHelper.divergingT(value, scale, higherIsBetter);

    return t >= 0
      ? getLerpRGB(NEUTRAL_RGB, HOT_RGB, t)
      : getLerpRGB(NEUTRAL_RGB, COLD_RGB, -t);
  }

  /**
   * Baseline gray stays nearly clear. Opacity rises only as the value
   * moves toward blue or red.
   * On a light card a 0.5 fill stays pastel and misses the legend, so
   * `isLightMode` runs the same distance up to a solid scale color.
   */
  static getDivergingOpacity(
    value: number,
    scale: { min: number; baseline: number; max: number },
    isHigherBetter: boolean,
    isLightMode: boolean
  ): number {
    const distance = Math.abs(
      SessionRecapHelper.divergingT(value, scale, isHigherBetter)
    );

    if (isLightMode) {
      return 0.16 + 0.84 * distance;
    }

    return 0.08 + 0.42 * distance;
  }

  static getDivergingColor(
    value: number,
    scale: { min: number; baseline: number; max: number },
    higherIsBetter = true
  ): string {
    const [r, g, b] = SessionRecapHelper.getDivergingRGB(
      value,
      scale,
      higherIsBetter
    );
    return `rgb(${r}, ${g}, ${b})`;
  }

  static getPitchTypeColor(type: PitchType | undefined): string {
    if (!type) {
      return NEUTRAL_RGB_HEX;
    }

    const index = PITCH_TYPE_ORDER.indexOf(type);

    if (index === -1) {
      return NEUTRAL_RGB_HEX;
    }

    return PITCH_TYPE_PALETTE[index % PITCH_TYPE_PALETTE.length];
  }

  /** category key for the current colour-by, when the legend has discrete items */
  static getLegendKey = (
    recap: ISessionRecapHit,
    colorBy: RecapColorBy
  ): string | undefined => {
    switch (colorBy) {
      case RecapColorBy.PitchType: {
        return recap.hit.pitch?.type;
      }

      case RecapColorBy.Contact: {
        return recap.contactQuality;
      }

      case RecapColorBy.LaunchAngle: {
        return recap.battedBallType;
      }

      case RecapColorBy.ExitVelo: {
        return undefined;
      }

      case RecapColorBy.Result:
      default: {
        return recap.result;
      }
    }
  };

  static getColorForDot = (
    recap: ISessionRecapHit,
    colorBy: RecapColorBy
  ): IDotColor => {
    switch (colorBy) {
      case RecapColorBy.PitchType: {
        return {
          color: SessionRecapHelper.getPitchTypeColor(recap.hit.pitch?.type),
          muted: false,
        };
      }

      case RecapColorBy.Contact: {
        return recap.contactQuality
          ? {
              color: CONTACT_QUALITY_COLORS[recap.contactQuality],
              muted: false,
            }
          : MUTED;
      }

      case RecapColorBy.ExitVelo: {
        const ev = recap.isContact ? recap.hit.hit?.exitMPH : undefined;
        return isNil(ev)
          ? MUTED
          : {
              color: SessionRecapHelper.getDivergingColor(ev, EXIT_VELO_SCALE),
              muted: false,
            };
      }

      case RecapColorBy.LaunchAngle: {
        return recap.battedBallType
          ? {
              color: BATTED_BALL_TYPE_COLORS[recap.battedBallType],
              muted: false,
            }
          : MUTED;
      }

      case RecapColorBy.Result:
      default: {
        return recap.result
          ? {
              color: SESSION_HIT_RESULT_COLORS[recap.result],
              muted: false,
            }
          : MUTED;
      }
    }
  };

  static getLabelForColorBy = (value: RecapColorBy) =>
    MAP_COLOR_BY_TO_LABEL[value];

  static getLabelForBattedBallType = (value: BattedBallType) =>
    BATTERED_BALL_TYPE_LABELS[value];

  /** undefined => the legend is a gradient (exit velo) */
  static getLegendItems = (
    recaps: ISessionRecapHit[],
    colorBy: RecapColorBy
  ): ILegendItem[] | undefined => {
    switch (colorBy) {
      case RecapColorBy.PitchType: {
        const types = uniq(compact(recaps.map((r) => r.hit.pitch?.type)));

        return types.map((type) => ({
          key: type,
          label: type,
          color: SessionRecapHelper.getPitchTypeColor(type),
        }));
      }

      case RecapColorBy.Contact: {
        const present = new Set(recaps.map((r) => r.contactQuality));

        return Object.values(ContactQuality)
          .filter((v) => present.has(v))
          .map((v) => ({
            key: v,
            label: CONTACT_QUALITY_LABELS[v],
            color: CONTACT_QUALITY_COLORS[v],
          }));
      }

      case RecapColorBy.ExitVelo: {
        return undefined;
      }

      case RecapColorBy.LaunchAngle: {
        const present = new Set(recaps.map((r) => r.battedBallType));

        return Object.values(BattedBallType)
          .filter((v) => present.has(v))
          .map((v) => ({
            key: v,
            label: BATTERED_BALL_TYPE_LABELS[v],
            color: BATTED_BALL_TYPE_COLORS[v],
          }));
      }

      case RecapColorBy.Result:
      default: {
        const present = new Set(recaps.map((r) => r.result));

        return SESSION_HIT_RESULTS.filter((v) => present.has(v)).map((v) => ({
          key: v,
          label: SESSION_HIT_RESULT_LABELS[v],
          color: SESSION_HIT_RESULT_COLORS[v],
        }));
      }
    }
  };
}
